import * as Sentry from '@sentry/react'
import type { MembershipData, UserData } from '@workspace/api-backend'
import { OnboardingFlow } from '@workspace/seedforge-features/onboarding'
import { needsTermsAcceptance } from '@workspace/seedforge-shared'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { TermsAcceptanceDialog } from '../features/terms/components/terms-acceptance-dialog'
import { TermsAcceptanceStep } from '../features/terms/components/terms-acceptance-step'
import {
  useCurrentUserQuery,
  useInvestorConfigQuery,
  useMyMembershipsQuery,
  useSignInProvidersQuery,
  useStartupConfigQuery,
} from '../hooks/api'
import { auth, useSession } from '../lib/auth'
import { identifyUser, trackEvent } from '../lib/gtag'
import { createOnboardingApiFromSdk } from '../lib/onboarding-api'
import { signupQuestionSource, useReportSignupSource } from '../lib/signup-source'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AppContextValue {
  user: UserData
  memberships: MembershipData[]
}

// ─── Context ─────────────────────────────────────────────────────────────────

export const AppContext = createContext<AppContextValue | null>(null)

export function useAppContext() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useAppContext must be used within /app')
  return ctx
}

// ─── Provider (handles auth + onboarding) ────────────────────────────────────

function useInviteFromUrl(api: ReturnType<typeof createOnboardingApiFromSdk>) {
  const [inviteToken, setInviteToken] = useState<string | undefined>()
  const [inviteEmail, setInviteEmail] = useState<string | undefined>()
  const [inviteRole, setInviteRole] = useState<'founder' | 'investor' | undefined>()
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('invite')
    if (!token) {
      setChecked(true)
      return
    }

    setInviteToken(token)

    api
      .lookupInvite(token)
      .then((data) => {
        setInviteEmail(data.email)
        setInviteRole(data.investorName ? 'investor' : 'founder')
      })
      .catch(() => {
        setInviteToken(undefined)
      })
      .finally(() => setChecked(true))
  }, [api])

  return { inviteToken, inviteEmail, inviteRole, checked }
}

// Google returns `403 disallowed_useragent` inside these in-app browsers with
// no way back from it, so the button is hidden there rather than offering a
// dead end. LinkedIn is a primary traffic source for this app.
const IN_APP_BROWSER_UA = /LinkedInApp|FBAN|FBAV|Instagram/

// Fixed copy per better-auth error code (never the raw `?error=` value — it's
// attacker-controlled query-string text). Codes come from oauth2/state.mjs
// (expired/mismatched state), the callback route (cancelled consent),
// googleUserInfo's Gmail/Workspace-only policy, and our own gate.
const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Google sign-in was cancelled.',
  please_restart_the_process: 'That took too long — please try again.',
  state_mismatch: 'That took too long — please try again.',
  unable_to_get_user_info:
    "This Google account can't be used here — continue with your email instead.",
  // Fires for the admin lockout (databaseHooks.session.create.before
  // returning false) as well as any other session-creation failure.
  unable_to_create_session:
    "This Google account can't be used here — continue with your email instead.",
  invalid_callback_request: 'That took too long — please try again.',
  email_not_found: "This Google account can't be used here — continue with your email instead.",
  "email_doesn't_match":
    "This Google account can't be used here — continue with your email instead.",
  google_disabled: 'Google sign-in is unavailable — continue with your email instead.',
}
const GOOGLE_ERROR_FALLBACK = 'Google sign-in failed — please try again.'

// GA signup_completed/login_completed (lib/gtag.ts). "New" is a heuristic —
// better-auth doesn't hand back an isNewUser flag — a user record created in
// the last minute is treated as this sign-in's own signup.
const NEW_USER_WINDOW_MS = 60_000
function isFreshAccount(createdAt: string | Date | undefined): boolean {
  if (!createdAt) return false
  const created = new Date(createdAt).getTime()
  return Number.isFinite(created) && Date.now() - created < NEW_USER_WINDOW_MS
}

// Google's sign-in is a full-page redirect, so there is no in-JS success
// callback to fire the GA event from — only the reload on the other side of
// it. This flag, set right before the redirect and read on the next mount,
// is what tells that reload apart from an ordinary refresh of an
// already-signed-in tab (which must NOT re-fire the event).
const GOOGLE_AUTH_PENDING_KEY = 'sf_google_auth_pending'

/** Reads and strips a better-auth `?error=` param, returning the fixed
 * message to show (once) — never the raw code, and never left in the URL for
 * a reload to show again. */
function useGoogleErrorNotice() {
  const [notice, setNotice] = useState<string | undefined>()

  useEffect(() => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get('error')
    if (!code) return
    setNotice(GOOGLE_ERROR_MESSAGES[code] ?? GOOGLE_ERROR_FALLBACK)
    url.searchParams.delete('error')
    url.searchParams.delete('error_description')
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
  }, [])

  return notice
}

function AcceptInviteAndRedirect({
  api,
  inviteToken,
}: {
  api: ReturnType<typeof createOnboardingApiFromSdk>
  inviteToken: string
}) {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .acceptInvite(inviteToken)
      .then((result) => {
        if (result.startupId) {
          window.location.href = `/app/startup/${result.startupId}`
        } else if (result.investorId) {
          window.location.href = `/app/investor/${result.investorId}`
        } else {
          window.location.href = '/app'
        }
      })
      .catch(() => {
        setError('Failed to accept invite. The link may be expired or invalid.')
      })
  }, [api, inviteToken])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-sm text-destructive">{error}</p>
          <a href="/app" className="mt-4 inline-block text-sm text-muted-foreground underline">
            Go to dashboard
          </a>
        </div>
      </div>
    )
  }

  return null
}

export function AppRouteGuard({ children }: { children: React.ReactNode }) {
  const { data: session, isPending: isSessionPending } = useSession()

  const { data: userData, isPending: isUserPending } = useCurrentUserQuery({
    enabled: !!session,
  })
  useReportSignupSource(session?.user?.id, session?.user?.createdAt)

  const { data: memberships, isPending: isMembershipsPending } = useMyMembershipsQuery({
    enabled: !!session,
  })

  const api = useMemo(() => createOnboardingApiFromSdk(), [])
  const { inviteToken, inviteEmail, inviteRole, checked: inviteChecked } = useInviteFromUrl(api)

  // Only worth fetching pre-login — an onboarded user never sees this button.
  const { data: providers, isPending: isProvidersPending } = useSignInProvidersQuery({
    enabled: !isSessionPending && !session,
  })
  const googleNotice = useGoogleErrorNotice()

  // GA signup_completed/login_completed for a Google sign-in — fires once,
  // only on the reload that comes back from a redirect this tab itself
  // started (see GOOGLE_AUTH_PENDING_KEY). The email path fires synchronously
  // from verifyOtp above instead, since it never leaves the page.
  useEffect(() => {
    if (!session?.user?.id) return
    let pending: string | null = null
    try {
      pending = sessionStorage.getItem(GOOGLE_AUTH_PENDING_KEY)
    } catch {
      // Can't read it, so there's nothing to attribute to Google here.
    }
    if (!pending) return
    try {
      sessionStorage.removeItem(GOOGLE_AUTH_PENDING_KEY)
    } catch {}
    trackEvent(isFreshAccount(session.user.createdAt) ? 'signup_completed' : 'login_completed', {
      method: 'google',
    })
  }, [session?.user?.id, session?.user?.createdAt])

  // Identify user in GA + Sentry once session + user data are confirmed
  useEffect(() => {
    if (!session?.user?.id || !userData?.onboardingCompleted) return
    const userType = userData.userType === 'INVESTOR' ? 'investor' : 'startup'
    identifyUser(session.user.id, { user_type: userType })
    Sentry.setUser({
      id: session.user.id,
      email: session.user.email,
      userType,
    })
  }, [session?.user?.id, session?.user?.email, userData?.userType, userData?.onboardingCompleted])
  const { data: investorConfig } = useInvestorConfigQuery({ enabled: !!session })
  const { data: startupConfig } = useStartupConfigQuery({ enabled: !!session })

  const sendOtp = async (email: string) => {
    await auth.emailOtp.sendVerificationOtp({ email, type: 'sign-in' })
  }

  const verifyOtp = async (email: string, otp: string) => {
    const res = await auth.signIn.emailOtp({ email, otp })
    if (!res.error) {
      trackEvent(
        isFreshAccount(res.data?.user?.createdAt) ? 'signup_completed' : 'login_completed',
        {
          method: 'email',
        }
      )
    }
    return !res.error
  }

  // On success, better-auth's client redirects the browser to Google itself
  // (fetch-plugins.mjs' redirectPlugin) — this only returns when it didn't,
  // e.g. the flag was turned off between the button rendering and the click.
  // Deep-links (e.g. an email pointing at /app/startup/:id) survive because
  // the current URL — not a fixed /app — is the callback target.
  const signInWithGoogle = useCallback(async () => {
    const url = new URL(window.location.href)
    url.searchParams.delete('error')
    url.searchParams.delete('error_description')
    try {
      sessionStorage.setItem(GOOGLE_AUTH_PENDING_KEY, '1')
    } catch {
      // Private browsing etc. — worst case the GA event for this sign-in is
      // missed, never that a login is blocked over it.
    }
    const res = await auth.signIn.social({ provider: 'google', callbackURL: url.toString() })
    if (res.error) {
      // Never actually redirected — this pending flag would otherwise
      // misattribute a later, unrelated sign-in to Google.
      try {
        sessionStorage.removeItem(GOOGLE_AUTH_PENDING_KEY)
      } catch {}
    }
    return res.error ? { error: res.error } : undefined
  }, [])

  const isInAppBrowser = IN_APP_BROWSER_UA.test(navigator.userAgent)
  const onGoogleSignIn = providers?.google && !isInAppBrowser ? signInWithGoogle : undefined

  const handleSignOut = useCallback(async () => {
    Sentry.setUser(null)
    await auth.signOut()
    window.location.href = '/app'
  }, [])

  // Only block rendering on initial load, not on background refetch (tab focus)
  const initialLoadDone = useRef(false)
  const isInitialLoading =
    !inviteChecked ||
    isSessionPending ||
    (session && (isUserPending || isMembershipsPending)) ||
    (!isSessionPending && !session && isProvidersPending)
  if (isInitialLoading && !initialLoadDone.current) {
    return null
  }
  if (!isInitialLoading) {
    initialLoadDone.current = true
  }

  // A signed-in account that has not accepted the current terms sees the gate over whatever comes next
  // An onboarded account accepts over the app; one still onboarding accepts as the first onboarding step
  const needsTerms = !!session && !!userData && needsTermsAcceptance(userData.termsVersion)
  const termsDialog = needsTerms && userData?.onboardingCompleted ? <TermsAcceptanceDialog /> : null
  const termsStep =
    needsTerms && !userData?.onboardingCompleted ? <TermsAcceptanceStep /> : undefined

  // Already onboarded user with invite token → accept invite directly, skip onboarding flow
  if (session && userData?.onboardingCompleted && inviteToken) {
    // The invite is accepted only after the terms, so the gate stands alone here
    if (termsDialog) return termsDialog
    return <AcceptInviteAndRedirect api={api} inviteToken={inviteToken} />
  }

  // Not yet onboarded (or new user via invite) → OnboardingFlow handles login + onboarding
  // Only show onboarding when we know for sure (userData loaded and not completed, or no session)
  if (!session || (userData && !userData.onboardingCompleted)) {
    const userRole = userData?.userType
      ? (userData.userType.toLowerCase() as 'founder' | 'investor')
      : null
    return (
      <OnboardingFlow
        api={api}
        sendOtp={sendOtp}
        verifyOtp={verifyOtp}
        onGoogleSignIn={onGoogleSignIn}
        notice={googleNotice}
        onComplete={(result) => {
          if (result.startupId) {
            window.location.href = `/app/startup/${result.startupId}`
          } else if (result.investorId) {
            window.location.href = `/app/investor/${result.investorId}`
          } else {
            window.location.href = '/app'
          }
        }}
        authenticated={!!session}
        userEmail={session?.user?.email}
        userName={session?.user?.name || undefined}
        userRole={userRole}
        onSignOut={handleSignOut}
        inviteToken={inviteToken}
        inviteEmail={inviteEmail}
        inviteRole={inviteRole}
        areaOptions={investorConfig?.investmentAreas}
        regionOptions={investorConfig?.investmentRegions}
        stageOptions={startupConfig?.productStages}
        raiseStageOptions={startupConfig?.raiseStages}
        categoryOptions={startupConfig?.categories?.map((c) => ({
          value: c.id,
          label: c.name,
        }))}
        signupQuestion={userData?.attributionAnsweredAt ? undefined : signupQuestionSource}
        termsStep={termsStep}
      />
    )
  }

  // Wait for memberships
  if (isMembershipsPending || !userData) return null

  // Fully authenticated + onboarded
  return (
    <AppContext.Provider value={{ user: userData, memberships: memberships ?? [] }}>
      {children}
      {termsDialog}
    </AppContext.Provider>
  )
}
